import { useMemo } from 'react'
import {
  ENTITY_TYPES,
  IAppointmentInfo,
  CardClosingRequirements,
} from '@sales-center/common'
import { checkSchedulingComplete } from '@user/store/scheduling'
import { useAppSelector } from '@user/store/hooks'
import {
  selectEntityAppointmentsSearch,
  selectEntityAppointmentsSearchLoading,
} from '@user/store/scheduling/searchSlice'
import { filterByEntity } from '@sales-center/common'
import { permissionDenied } from '@user/store/authorization/permissions'

type Props = {
  entityId: number
  recordClosed: boolean
  servicesCount: number
  entityType: ENTITY_TYPES
  externalLocationId: string
  services: IAppointmentInfo[]
  hasValidLocationAddress: boolean
  requirementsAndCounts: CardClosingRequirements
  canUpdate: boolean
}

export function useData(props: Props) {
  const {
    services,
    entityId,
    entityType,
    recordClosed,
    servicesCount,
    externalLocationId,
    requirementsAndCounts,
    hasValidLocationAddress,
    canUpdate,
  } = props

  const disabledTooltip = useMemo(() => {
    if (!canUpdate) return permissionDenied

    const typeName = entityType.toLowerCase()

    if (!Boolean(externalLocationId))
      return `Match this ${typeName} to a location to schedule`

    if (!hasValidLocationAddress)
      return 'Add a valid service location address to schedule'

    if (servicesCount === 0)
      return `Add services to this ${typeName} to schedule`

    if (recordClosed)
      return `You cannot schedule appointments for a ${
        entityType == ENTITY_TYPES.Lead ? 'disqualified' : 'closed'
      } ${typeName}.`
  }, [
    entityType,
    recordClosed,
    servicesCount,
    externalLocationId,
    hasValidLocationAddress,
    canUpdate,
  ])

  const countLoading = useAppSelector(selectEntityAppointmentsSearchLoading)
  const allAppointments = useAppSelector(selectEntityAppointmentsSearch)

  const appointments = useMemo(
    () => filterByEntity(allAppointments, entityId, entityType),
    [allAppointments, entityId, entityType],
  )

  const appointmentCount = appointments.length
  const isDisabled = !canUpdate || !!disabledTooltip

  const schedulingComplete = useMemo(
    () =>
      checkSchedulingComplete(services, requirementsAndCounts) &&
      appointmentCount > 0,
    [requirementsAndCounts, services, appointmentCount],
  )

  return {
    countLoading,
    disabledTooltip,
    appointmentCount,
    schedulingComplete,
    disabled: isDisabled,
  }
}
